import { lazy, type ComponentType } from "react";
import type { LogoBarProps } from "./LogoBar";

// Blocks are lazy-loaded so each page ships only the block components it
// actually renders, instead of the entire ~150-block registry. SSR still
// renders the real markup (BlockRenderer wraps each block in <Suspense>),
// so content is present in the server HTML for SEO and initial paint.
function lazyBlock(
  loader: () => Promise<{ default: ComponentType<any> }>,
): ComponentType<any> {
  return lazy(loader) as unknown as ComponentType<any>;
}

// === Feature blocks ===
const FeaturesCardGridIcons = lazyBlock(() => import("./FeaturesCardGridIcons").then((m) => ({ default: m.FeaturesCardGridIcons })));
const FlexYourAlpha = lazyBlock(() => import("./FlexYourAlpha").then((m) => ({ default: m.FlexYourAlpha })));
const FeaturesIconCardsLabeled = lazyBlock(() => import("./FeaturesIconCardsLabeled").then((m) => ({ default: m.FeaturesIconCardsLabeled })));
const FeaturesHowItWorks = lazyBlock(() => import("./FeaturesHowItWorks").then((m) => ({ default: m.FeaturesHowItWorks })));
const FeaturesAlternatingCards = lazyBlock(() => import("./FeaturesAlternatingCards").then((m) => ({ default: m.FeaturesAlternatingCards })));
const FeaturesVariableGrid = lazyBlock(() => import("./FeaturesVariableGrid").then((m) => ({ default: m.FeaturesVariableGrid })));
const FeaturesThreeColOverlapImage = lazyBlock(() => import("./FeaturesThreeColOverlapImage").then((m) => ({ default: m.FeaturesThreeColOverlapImage })));
const FeaturesCardGridGradient = lazyBlock(() => import("./FeaturesCardGridGradient").then((m) => ({ default: m.FeaturesCardGridGradient })));
const FeaturesTwoColumnImage = lazyBlock(() => import("./FeaturesTwoColumnImage").then((m) => ({ default: m.FeaturesTwoColumnImage })));
const FeaturesTwoColumnCards = lazyBlock(() => import("./FeaturesTwoColumnCards").then((m) => ({ default: m.FeaturesTwoColumnCards })));
const FeaturesTwoColText = lazyBlock(() => import("./FeaturesTwoColText").then((m) => ({ default: m.FeaturesTwoColText })));
const FeaturesSplitImageBullets = lazyBlock(() => import("./FeaturesSplitLayout").then((m) => ({ default: m.FeaturesSplitImageBullets })));
const FeaturesSplitImageGradient = lazyBlock(() => import("./FeaturesSplitLayout").then((m) => ({ default: m.FeaturesSplitImageGradient })));
const FeaturesSplitImageList = lazyBlock(() => import("./FeaturesSplitLayout").then((m) => ({ default: m.FeaturesSplitImageList })));
const FeaturesSplitDark = lazyBlock(() => import("./FeaturesSplitLayout").then((m) => ({ default: m.FeaturesSplitDark })));
const FeaturesAlternatingImage = lazyBlock(() => import("./FeaturesSplitLayout").then((m) => ({ default: m.FeaturesAlternatingImage })));
const FeaturesSplitImageCards = lazyBlock(() => import("./FeaturesSplitImageCards").then((m) => ({ default: m.FeaturesSplitImageCards })));
const FeaturesCenteredCards = lazyBlock(() => import("./FeaturesCenteredCards").then((m) => ({ default: m.FeaturesCenteredCards })));
const FeaturesColumnsText = lazyBlock(() => import("./FeaturesColumnsText").then((m) => ({ default: m.FeaturesColumnsText })));
const FeaturesChecklist = lazyBlock(() => import("./FeaturesChecklist").then((m) => ({ default: m.FeaturesChecklist })));
const FeaturesNumberedSteps = lazyBlock(() => import("./FeaturesNumberedSteps").then((m) => ({ default: m.FeaturesNumberedSteps })));
const FeaturesNumberedList = lazyBlock(() => import("./FeaturesNumberedList").then((m) => ({ default: m.FeaturesNumberedList })));
const FeaturesTimeline = lazyBlock(() => import("./FeaturesTimeline").then((m) => ({ default: m.FeaturesTimeline })));
const FeaturesTabbedContent = lazyBlock(() => import("./FeaturesTabbedContent").then((m) => ({ default: m.FeaturesTabbedContent })));
const FeaturesTabbedContentV2 = lazyBlock(() => import("./FeaturesTabbedContentV2").then((m) => ({ default: m.FeaturesTabbedContentV2 })));
const FeaturesTabbedHover = lazyBlock(() => import("./FeaturesTabbedHover").then((m) => ({ default: m.FeaturesTabbedHover })));
const FeaturesStackedCards = lazyBlock(() => import("./FeaturesStackedCards").then((m) => ({ default: m.FeaturesStackedCards })));
const FeaturesIconGridCompact = lazyBlock(() => import("./FeaturesIconGridCompact").then((m) => ({ default: m.FeaturesIconGridCompact })));
const FeaturesIconList = lazyBlock(() => import("./FeaturesIconList").then((m) => ({ default: m.FeaturesIconList })));
const FeaturesStepsDark = lazyBlock(() => import("./FeaturesStepsDark").then((m) => ({ default: m.FeaturesStepsDark })));
const FeatureProblemDark = lazyBlock(() => import("./FeatureProblemDark").then((m) => ({ default: m.FeatureProblemDark })));
const CatalogBandMosaic = lazyBlock(() => import("./CatalogBandMosaic").then((m) => ({ default: m.CatalogBandMosaic })));
const HarnessDemoPanels = lazyBlock(() => import("./HarnessDemoPanels").then((m) => ({ default: m.HarnessDemoPanels })));
const HarnessCategoryShowcase = lazyBlock(() => import("./HarnessCategoryShowcase").then((m) => ({ default: m.HarnessCategoryShowcase })));
const FeatureSplitImage = lazyBlock(() => import("./FeatureSplitImage").then((m) => ({ default: m.FeatureSplitImage })));
const FeatureCenteredImage = lazyBlock(() => import("./FeatureCenteredImage").then((m) => ({ default: m.FeatureCenteredImage })));
const ImageShowcase = lazyBlock(() => import("./ImageShowcase").then((m) => ({ default: m.ImageShowcase })));
const FeaturesEvenGrid = lazyBlock(() => import("./FeaturesEvenGrid").then((m) => ({ default: m.FeaturesEvenGrid })));
const FeatureFullWidthImage = lazyBlock(() => import("./FeatureFullWidthImage").then((m) => ({ default: m.FeatureFullWidthImage })));

// === CTA blocks ===
const CTACenteredDivider = lazyBlock(() => import("./CTACenteredDivider").then((m) => ({ default: m.CTACenteredDivider })));
const CTABannerDark = lazyBlock(() => import("./CTABannerDark").then((m) => ({ default: m.CTABannerDark })));
const CTACardGradient = lazyBlock(() => import("./CTACardGradient").then((m) => ({ default: m.CTACardGradient })));
const CTASimpleCentered = lazyBlock(() => import("./CTASimpleCentered").then((m) => ({ default: m.CTASimpleCentered })));
const CTAMinimal = lazyBlock(() => import("./CTAMinimal").then((m) => ({ default: m.CTAMinimal })));
const CTAFullWidthDark = lazyBlock(() => import("./CTAFullWidthDark").then((m) => ({ default: m.CTAFullWidthDark })));

// === Latest/resources ===
const LatestCardsDark = lazyBlock(() => import("./LatestCardsDark").then((m) => ({ default: m.LatestCardsDark })));

// === Hero blocks ===
const HeroSplitLabel = lazyBlock(() => import("./HeroSplitLabel").then((m) => ({ default: m.HeroSplitLabel })));
const HeroComparison = lazyBlock(() => import("./HeroComparison").then((m) => ({ default: m.HeroComparison })));
const HeroSplitBullets = lazyBlock(() => import("./HeroSplitBullets").then((m) => ({ default: m.HeroSplitBullets })));
const HeroRoundedCard = lazyBlock(() => import("./HeroRoundedCard").then((m) => ({ default: m.HeroRoundedCard })));
const HeroSplitTabbed = lazyBlock(() => import("./HeroSplitTabbed").then((m) => ({ default: m.HeroSplitTabbed })));
const HeroSplitDarkBg = lazyBlock(() => import("./HeroSplitDarkBg").then((m) => ({ default: m.HeroSplitDarkBg })));
const HeroFormHubspot = lazyBlock(() => import("./HeroFormHubspot").then((m) => ({ default: m.HeroFormHubspot })));
const HeroFormCalendly = lazyBlock(() => import("./HeroFormCalendly").then((m) => ({ default: m.HeroFormCalendly })));
const HeroDarkForm = lazyBlock(() => import("./HeroDarkForm").then((m) => ({ default: m.HeroDarkForm })));
const HeroCenteredGradient = lazyBlock(() => import("./HeroCenteredGradient").then((m) => ({ default: m.HeroCenteredGradient })));
const HeroCenteredLabel = lazyBlock(() => import("./HeroCenteredLabel").then((m) => ({ default: m.HeroCenteredLabel })));
const HeroCenteredWide = lazyBlock(() => import("./HeroCenteredWide").then((m) => ({ default: m.HeroCenteredWide })));
const HeroDarkSplit = lazyBlock(() => import("./HeroDarkSplit").then((m) => ({ default: m.HeroDarkSplit })));
const HeroLabelOverflowImage = lazyBlock(() => import("./HeroLabelOverflowImage").then((m) => ({ default: m.HeroLabelOverflowImage })));
const HeroSplitDarkSubheading = lazyBlock(() => import("./HeroSplitDarkSubheading").then((m) => ({ default: m.HeroSplitDarkSubheading })));
const HeroSplitVideo = lazyBlock(() => import("./HeroSplitVideo").then((m) => ({ default: m.HeroSplitVideo })));
const HeroTabbedImage = lazyBlock(() => import("./HeroTabbedImage").then((m) => ({ default: m.HeroTabbedImage })));
const HeroSplitImageLeft = lazyBlock(() => import("./HeroSplitImageLeft").then((m) => ({ default: m.HeroSplitImageLeft })));
const HeroSplitImageRight = lazyBlock(() => import("./HeroSplitImageRight").then((m) => ({ default: m.HeroSplitImageRight })));
const HeroDarkGradient = lazyBlock(() => import("./HeroDarkGradient").then((m) => ({ default: m.HeroDarkGradient })));
const HeroDarkVideo = lazyBlock(() => import("./HeroDarkVideo").then((m) => ({ default: m.HeroDarkVideo })));
const PodcastEpisode = lazyBlock(() => import("./PodcastEpisode").then((m) => ({ default: m.PodcastEpisode })));

// === Logo blocks ===
const LogoBar = lazyBlock(() => import("./LogoBar").then((m) => ({ default: m.LogoBar })));
const SponsorLogoRow = lazyBlock(() => import("./SponsorLogoRow").then((m) => ({ default: m.SponsorLogoRow })));

// === Stats blocks ===
const StatsColumnCards = lazyBlock(() => import("./StatsColumnCards").then((m) => ({ default: m.StatsColumnCards })));
const StatsLargeNumbers = lazyBlock(() => import("./StatsLargeNumbers").then((m) => ({ default: m.StatsLargeNumbers })));
const StatsHorizontal = lazyBlock(() => import("./StatsHorizontal").then((m) => ({ default: m.StatsHorizontal })));
const StatsWithTestimonials = lazyBlock(() => import("./StatsWithTestimonials").then((m) => ({ default: m.StatsWithTestimonials })));

// === FAQ ===
const FaqAccordion = lazyBlock(() => import("./FaqAccordion").then((m) => ({ default: m.FaqAccordion })));

// === Testimonial blocks ===
const TestimonialCardPurpleWide = lazyBlock(() => import("./TestimonialCardPurpleWide").then((m) => ({ default: m.TestimonialCardPurpleWide })));
const TestimonialCardDarkOverlay = lazyBlock(() => import("./TestimonialCardDarkOverlay").then((m) => ({ default: m.TestimonialCardDarkOverlay })));
const TestimonialsGridCards = lazyBlock(() => import("./TestimonialsGridCards").then((m) => ({ default: m.TestimonialsGridCards })));
const TestimonialsGridOffset = lazyBlock(() => import("./TestimonialsGridOffset").then((m) => ({ default: m.TestimonialsGridOffset })));
const TestimonialCardLight = lazyBlock(() => import("./TestimonialCardLight").then((m) => ({ default: m.TestimonialCardLight })));
const TestimonialCardBlue = lazyBlock(() => import("./TestimonialCardBlue").then((m) => ({ default: m.TestimonialCardBlue })));
const TestimonialCardMinimal = lazyBlock(() => import("./TestimonialCardMinimal").then((m) => ({ default: m.TestimonialCardMinimal })));
const TestimonialCardStat = lazyBlock(() => import("./TestimonialCardStat").then((m) => ({ default: m.TestimonialCardStat })));
const TestimonialHeroStat = lazyBlock(() => import("./TestimonialHeroStat").then((m) => ({ default: m.TestimonialHeroStat })));
const TestimonialQuoteCentered = lazyBlock(() => import("./TestimonialQuoteCentered").then((m) => ({ default: m.TestimonialQuoteCentered })));

// === Table ===
const TableComparison = lazyBlock(() => import("./TableComparison").then((m) => ({ default: m.TableComparison })));

// === Text ===
const TextSectionSimple = lazyBlock(() => import("./TextSectionSimple").then((m) => ({ default: m.TextSectionSimple })));

// === Integration blocks ===
const IntegrationsShowcaseDark = lazyBlock(() => import("./IntegrationsShowcaseDark").then((m) => ({ default: m.IntegrationsShowcaseDark })));
const IntegrationsCategoryGrid = lazyBlock(() => import("./IntegrationsCategoryGrid").then((m) => ({ default: m.IntegrationsCategoryGrid })));
const IntegrationsLatestCards = lazyBlock(() => import("./IntegrationsLatestCards").then((m) => ({ default: m.IntegrationsLatestCards })));
const IntegrationsLatestCardsV2 = lazyBlock(() => import("./IntegrationsLatestCardsV2").then((m) => ({ default: m.IntegrationsLatestCardsV2 })));

// === Registration/Forms ===
const RegisterCtaHubspot = lazyBlock(() => import("./RegisterCtaHubspot").then((m) => ({ default: m.RegisterCtaHubspot })));
const FormPartnership = lazyBlock(() => import("./FormPartnership").then((m) => ({ default: m.FormPartnership })));

// === Reports ===
const ReportDownload = lazyBlock(() => import("./ReportDownload").then((m) => ({ default: m.ReportDownload })));
const ReportDownloadV2 = lazyBlock(() => import("./ReportDownloadV2").then((m) => ({ default: m.ReportDownloadV2 })));
const ReportDownload2026 = lazyBlock(() => import("./ReportDownload2026").then((m) => ({ default: m.ReportDownload2026 })));
const AgenticAdaptationPlaybook = lazyBlock(() => import("./AgenticAdaptationPlaybook").then((m) => ({ default: m.AgenticAdaptationPlaybook })));

// === Trials ===
const TrialCtaSimple = lazyBlock(() => import("./TrialCtaSimple").then((m) => ({ default: m.TrialCtaSimple })));
const TrialCtaGradient = lazyBlock(() => import("./TrialCtaGradient").then((m) => ({ default: m.TrialCtaGradient })));

// === Conference ===
import { ConferenceHero } from "./ConferenceHero";
import { ConferenceSchedule } from "./ConferenceSchedule";
import { ConferenceSpeakers } from "./ConferenceSpeakers";
import { ConferenceDetails } from "./ConferenceDetails";
import { ConferenceCta } from "./ConferenceCta";
import { ConferenceWeekHero } from "./ConferenceWeekHero";
import { ConferenceWeekList } from "./ConferenceWeekList";
import { HostEmceeSection } from "./HostEmceeSection";
import { RsaHero2026 } from "./RsaHero2026";
import { RsaIntroSplit } from "./RsaIntroSplit";
import { RsaEventRow } from "./RsaEventRow";
import { TransformHero } from "./TransformHero";
import { AuthorFormSection } from "./AuthorFormSection";
import { TransformSpeakers } from "./TransformSpeakers";
import { TransformCountdown } from "./TransformCountdown";
import { TransformRegister } from "./TransformRegister";
import { TransformVipRegister } from "./TransformVipRegister";
import { TransformThankYou } from "./TransformThankYou";
// Lazy like the rest of the registry (the static imports above are the
// exception, not the pattern) — this one only ever renders on the Transform
// page, so it has no business in the chunk every block page loads.
const TransformSessions = lazyBlock(() => import("./TransformSessions").then((m) => ({ default: m.TransformSessions })));
// Same reasoning: the agenda block only ever renders on /c1-transform-2026/agenda.
const TransformAgenda = lazyBlock(() => import("./TransformAgenda").then((m) => ({ default: m.TransformAgenda })));

// === New block components ===
const HeroCenteredDark = lazyBlock(() => import("./HeroCenteredDark").then((m) => ({ default: m.HeroCenteredDark })));
const HeroSplitDarkLabel = lazyBlock(() => import("./HeroSplitDarkLabel").then((m) => ({ default: m.HeroSplitDarkLabel })));
const FeaturesCardGridDark = lazyBlock(() => import("./FeaturesCardGridDark").then((m) => ({ default: m.FeaturesCardGridDark })));
const FeaturesGrid3ColIcons = lazyBlock(() => import("./FeaturesGrid3ColIcons").then((m) => ({ default: m.FeaturesGrid3ColIcons })));
const FeaturesGrid4Col = lazyBlock(() => import("./FeaturesGrid4Col").then((m) => ({ default: m.FeaturesGrid4Col })));
const CTASplitImage = lazyBlock(() => import("./CTASplitImage").then((m) => ({ default: m.CTASplitImage })));
const TestimonialsSliderDark = lazyBlock(() => import("./TestimonialsSliderDark").then((m) => ({ default: m.TestimonialsSliderDark })));
const TrialCtaDark = lazyBlock(() => import("./TrialCtaDark").then((m) => ({ default: m.TrialCtaDark })));
const AuthorProfile = lazyBlock(() => import("./AuthorProfile").then((m) => ({ default: m.AuthorProfile })));
const CTACenteredButtons = lazyBlock(() => import("./CTACenteredButtons").then((m) => ({ default: m.CTACenteredButtons })));
const StatsCentered = lazyBlock(() => import("./StatsCentered").then((m) => ({ default: m.StatsCentered })));
const ResourcesHub = lazyBlock(() => import("./ResourcesHub").then((m) => ({ default: m.ResourcesHub })));

// === IGA sections ===
const IGASection1 = lazyBlock(() => import("./IGASection1").then((m) => ({ default: m.IGASection1 })));
const IGASection2 = lazyBlock(() => import("./IGASection2").then((m) => ({ default: m.IGASection2 })));
const IGASection3 = lazyBlock(() => import("./IGASection3").then((m) => ({ default: m.IGASection3 })));
const FeaturesAccordionImage = lazyBlock(() => import("./FeaturesAccordionImage").then((m) => ({ default: m.FeaturesAccordionImage })));

// === Unique pages ===
const IdentityMaturityAssessment = lazyBlock(() => import("./IdentityMaturityAssessment").then((m) => ({ default: m.IdentityMaturityAssessment })));
const IgadLanding = lazyBlock(() => import("./IgadLanding").then((m) => ({ default: m.IgadLanding })));
const AiNativeIdentitySection = lazyBlock(() => import("./AiNativeIdentitySection").then((m) => ({ default: m.AiNativeIdentitySection })));
const AprilFoolsGame = lazyBlock(() => import("./AprilFoolsGame").then((m) => ({ default: m.AprilFoolsGame })));
const HomeHero2026 = lazyBlock(() => import("./HomeHero2026").then((m) => ({ default: m.HomeHero2026 })));
const PartnersConsultingCatalog = lazyBlock(() => import("./PartnersConsultingCatalog").then((m) => ({ default: m.PartnersConsultingCatalog })));

// === Headless Identity Platform solution page ===
const FeatureDarkCardSplit = lazyBlock(() => import("./FeatureDarkCardSplit").then((m) => ({ default: m.FeatureDarkCardSplit })));
const FeaturesPinkIconCards = lazyBlock(() => import("./FeaturesPinkIconCards").then((m) => ({ default: m.FeaturesPinkIconCards })));
const FeaturesHeadingLeftCards = lazyBlock(() => import("./FeaturesHeadingLeftCards").then((m) => ({ default: m.FeaturesHeadingLeftCards })));
const FeaturesDarkFourColWireframe = lazyBlock(() => import("./FeaturesDarkFourColWireframe").then((m) => ({ default: m.FeaturesDarkFourColWireframe })));
const FeaturesDarkStepRow = lazyBlock(() => import("./FeaturesDarkStepRow").then((m) => ({ default: m.FeaturesDarkStepRow })));
const FeaturesHorizontalCardLinks = lazyBlock(() => import("./FeaturesHorizontalCardLinks").then((m) => ({ default: m.FeaturesHorizontalCardLinks })));
const TestimonialDarkPortrait = lazyBlock(() => import("./TestimonialDarkPortrait").then((m) => ({ default: m.TestimonialDarkPortrait })));
const CTAFullWidthPurple = lazyBlock(() => import("./CTAFullWidthPurple").then((m) => ({ default: m.CTAFullWidthPurple })));
const TextLede = lazyBlock(() => import("./TextLede").then((m) => ({ default: m.TextLede })));
const StatsDarkPromise = lazyBlock(() => import("./StatsDarkPromise").then((m) => ({ default: m.StatsDarkPromise })));
const SolutionWalkthroughBlade = lazyBlock(() => import("./SolutionWalkthroughBlade").then((m) => ({ default: m.SolutionWalkthroughBlade })));

// === Solutions Teams pages ===
const FeaturesProblemSplit = lazyBlock(() => import("./FeaturesProblemSplit").then((m) => ({ default: m.FeaturesProblemSplit })));
const FeaturesWhyDark = lazyBlock(() => import("./FeaturesWhyDark").then((m) => ({ default: m.FeaturesWhyDark })));
const FeaturesImpactSplit = lazyBlock(() => import("./FeaturesImpactSplit").then((m) => ({ default: m.FeaturesImpactSplit })));

// === Platform Overview page ===
const StatementSimple = lazyBlock(() => import("./StatementSimple").then((m) => ({ default: m.StatementSimple })));
const StatementSplitImage = lazyBlock(() => import("./StatementSplitImage").then((m) => ({ default: m.StatementSplitImage })));
const FeaturesSpotlightGrid = lazyBlock(() => import("./FeaturesSpotlightGrid").then((m) => ({ default: m.FeaturesSpotlightGrid })));

// === Integrations page animated diagram ===
const ConnectorFlowAnimation = lazyBlock(() => import("./ConnectorFlowAnimation").then((m) => ({ default: m.ConnectorFlowAnimation })));
const IntegrationsSpotlightGrid = lazyBlock(() => import("./IntegrationsSpotlightGrid").then((m) => ({ default: m.IntegrationsSpotlightGrid })));


// === C1 MCP Gateway direct-path animation ===
const XaaLaunchAnimation = lazyBlock(() => import("./XaaLaunchAnimation").then((m) => ({ default: m.XaaLaunchAnimation })));

// === C1 MCP Gateway page ===
const AgentAccessFabricAnimation = lazyBlock(() => import("./AgentAccessFabricAnimation").then((m) => ({ default: m.AgentAccessFabricAnimation })));
const FeaturesAccordionSplit = lazyBlock(() => import("./FeaturesAccordionSplit").then((m) => ({ default: m.FeaturesAccordionSplit })));

/**
 * Block registry: maps all 111 used block names to React components.
 */
export const BLOCK_REGISTRY: Record<string, ComponentType<any>> = {
  // === Features (9 components, 53 block types) ===
  "features-card-grid-icons": FeaturesCardGridIcons,
  "features-card-grid-muted": FeaturesCardGridIcons,
  "features-card-grid-gradient": FeaturesCardGridGradient,
  "features-icon-cards-labeled": FeaturesIconCardsLabeled,
  "features-icon-cards-labeled-v2": FeaturesIconCardsLabeled,
  "features-how-it-works": FeaturesHowItWorks,
  "features-how-it-works-v2": FeaturesHowItWorks,
  "features-how-it-works-v3": FeaturesHowItWorks,
  "features-alternating-cards": FeaturesAlternatingCards,
  "features-cards-gradient-bg": (p: any) => <FeaturesAlternatingCards {...p} background="gradient-dark" />,
  "features-alternating-image": FeaturesAlternatingImage,
  "features-variable-grid": FeaturesVariableGrid,
  "features-variable-grid-v2": (p: any) => <FeaturesVariableGrid {...p} variant="v2" />,
  "features-variable-grid-v3": (p: any) => <FeaturesVariableGrid {...p} variant="v3" />,
  "features-variable-grid-v4": (p: any) => <FeaturesVariableGrid {...p} variant="v4" />,
  "features-variable-grid-v5": (p: any) => <FeaturesVariableGrid {...p} variant="v5" />,
  "features-variable-grid-v6": (p: any) => <FeaturesVariableGrid {...p} variant="v6" />,
  "features-variable-grid-v7": (p: any) => <FeaturesVariableGrid {...p} variant="v7" />,
  "features-three-col-overlap-image": FeaturesThreeColOverlapImage,
  "features-two-column-image": FeaturesTwoColumnImage,
  "features-two-column-cards": FeaturesTwoColumnCards,
  "features-two-col-text": FeaturesTwoColText,
  "features-two-col-text-v2": (p: any) => <FeaturesTwoColText {...p} variant="v2" />,
  "features-split-image-bullets": FeaturesSplitImageBullets,
  "features-split-image-gradient": FeaturesSplitImageGradient,
  "features-split-image-list": FeaturesSplitImageList,
  "features-split-image-cards": FeaturesSplitImageCards,
  "features-split-dark": FeaturesSplitDark,
  "features-split-list": FeaturesSplitDark,
  "features-centered-cards": FeaturesCenteredCards,
  "features-columns-text": FeaturesColumnsText,
  "features-checklist": FeaturesChecklist,
  "features-numbered-steps": FeaturesNumberedSteps,
  "features-numbered-list": FeaturesNumberedList,
  "features-timeline": FeaturesTimeline,
  "features-tabbed-content": FeaturesTabbedContent,
  "features-tabbed-content-v2": FeaturesTabbedContentV2,
  "features-tabbed-hover": FeaturesTabbedHover,
  "features-stacked-cards": FeaturesStackedCards,
  "features-icon-grid-compact": FeaturesIconGridCompact,
  "features-icon-list": FeaturesIconList,
  "features-steps-dark": FeaturesStepsDark,
  "feature-problem-dark": FeatureProblemDark,
  "catalog-band-mosaic": CatalogBandMosaic,
  "harness-demo-panels": HarnessDemoPanels,
  "harness-category-showcase": HarnessCategoryShowcase,
  "feature-split-image": FeatureSplitImage,
  "feature-centered-image": FeatureCenteredImage,
  "image-showcase": ImageShowcase,
  "features-even-grid": FeaturesEvenGrid,
  "feature-full-width-image": FeatureFullWidthImage,

  // === CTAs (5 components) ===
  "cta-banner-dark": CTABannerDark,
  "cta-card-gradient": CTACardGradient,
  "cta-simple-centered": CTASimpleCentered,
  "cta-minimal": CTAMinimal,
  "cta-full-width-dark": CTAFullWidthDark,
  "cta-centered-divider": CTACenteredDivider,

  // === Latest (1 component, 3 variants) ===
  "latest-cards-dark": LatestCardsDark,
  "latest-card-grid": LatestCardsDark,
  "latest-cards-light": LatestCardsDark,

  // === Heroes (17 components) ===
  "hero-split-label": HeroSplitLabel,
  "hero-comparison": HeroComparison,
  "hero-comparison-alt": HeroComparison,
  "hero-split-bullets": HeroSplitBullets,
  "hero-rounded-card": HeroRoundedCard,
  "hero-split-tabbed": HeroSplitTabbed,
  "hero-split-dark-bg": (p: any) => <HeroSplitTabbed label={p.superheading} heading={p.heading?.replace(/<span[^>]*>([^<]*)<\/span>/g, "$1").replace(/<img[^>]*>/g, "")} copy={p.copy} image={p.image} button_primary={p.button_primary} />,
  "hero-form-hubspot": HeroFormHubspot,
  "hero-form-calendly": HeroFormCalendly,
  "hero-dark-form": HeroDarkForm,
  "hero-centered-gradient": HeroCenteredGradient,
  "hero-centered-label": HeroCenteredLabel,
  "hero-centered-wide": HeroCenteredWide,
  "hero-dark-split": HeroDarkSplit,
  "hero-label-overflow-image": HeroLabelOverflowImage,
  "hero-split-dark-subheading": HeroSplitDarkSubheading,
  "hero-split-video": HeroSplitVideo,
  "hero-tabbed-image": (p: any) => <HeroSplitTabbed label="Unified Identity Graph" heading="Complete identity visibility" copy={p.copy} image={p.image} button_primary={p.button_primary} />,
  "hero-split-image-left": HeroSplitImageLeft,
  "hero-split-image-right": HeroSplitImageRight,
  "hero-dark-gradient": HeroDarkGradient,
  "hero-dark-video": HeroDarkVideo,
  "podcast-episode": PodcastEpisode,

  // === Logos (1 component, wrapped with overlap for block pages) ===
  "logos-strip-inline": (p: LogoBarProps) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-strip-inline-v2": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-strip-dark": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-strip-bordered": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-grid-responsive": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} heading={undefined} /></div>,
  "logos-grid-muted": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} heading={undefined} /></div>,
  "logos-strip-centered": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-marquee": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-grid-simple": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-grid-dark": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "logos-strip-small": (p: any) => <div className={`${p.transparent ? "" : "-mt-14"} relative z-10 ${p.variant === "dark" && !p.transparent ? "bg-[#141414]" : ""}`}><LogoBar {...p} /></div>,
  "sponsor-logo-row": SponsorLogoRow,

  // === Stats (4 components) ===
  "stats-column-cards": StatsColumnCards,
  "stats-large-numbers": StatsLargeNumbers,
  "stats-horizontal": StatsHorizontal,
  "stats-with-testimonials": StatsWithTestimonials,

  // === FAQ (1 component) ===
  "faq-accordion": FaqAccordion,

  // === Testimonials (9 components) ===
  "testimonial-card-purple-wide": TestimonialCardPurpleWide,
  "testimonial-card-purple": TestimonialCardPurpleWide,
  "testimonial-card-dark-overlay": TestimonialCardDarkOverlay,
  "testimonials-grid-cards": TestimonialsGridCards,
  "testimonial-card-light": TestimonialCardLight,
  "testimonial-card-blue": TestimonialCardBlue,
  "testimonial-card-minimal": TestimonialCardMinimal,
  "testimonial-card-stat": TestimonialCardStat,
  "testimonial-hero-stat": TestimonialHeroStat,
  "testimonial-hero-stat-v2": TestimonialHeroStat,
  "testimonial-quote-centered": TestimonialQuoteCentered,
  "testimonials-grid-offset": TestimonialsGridOffset,

  // === Table (1 component) ===
  "table-comparison": TableComparison,

  // === Text (1 component) ===
  "text-section-simple": TextSectionSimple,

  // === Integrations (4 components) ===
  "integrations-showcase-dark": IntegrationsShowcaseDark,
  "integrations-category-grid": IntegrationsCategoryGrid,
  "integrations-latest-cards": IntegrationsLatestCards,
  "integrations-latest-cards-v2": IntegrationsLatestCardsV2,

  // === Registration/Forms (2 components) ===
  "register-cta-hubspot": RegisterCtaHubspot,
  "form-partnership": FormPartnership,

  // === Reports (3 components) ===
  "report-download": ReportDownload,
  "report-download-v2": ReportDownloadV2,
  "report-download-2026": ReportDownload2026,
  "agentic-adaptation-playbook": AgenticAdaptationPlaybook,

  // === Trials (3 components) ===
  "trial-cta-simple": TrialCtaSimple,
  "trial-cta-form": (p: any) => <TrialCtaSimple {...p} _usePhone />,
  "trial-cta-gradient": TrialCtaGradient,

  // === Conference (7 components) ===
  "conference-hero": ConferenceHero,
  "conference-schedule": ConferenceSchedule,
  "conference-speakers": ConferenceSpeakers,
  "conference-details": ConferenceDetails,
  "conference-cta": ConferenceCta,
  "conference-week-hero": ConferenceWeekHero,
  "conference-week-list": ConferenceWeekList,
  "host-emcee-section": HostEmceeSection,
  "rsa-hero-2026": RsaHero2026,
  "rsa-intro-split": RsaIntroSplit,
  "rsa-event-row": RsaEventRow,
  "transform-hero": TransformHero,
  "author-form-section": AuthorFormSection,
  "transform-speakers": TransformSpeakers,
  "transform-sessions": TransformSessions,
  "transform-agenda": TransformAgenda,
  "transform-countdown": TransformCountdown,
  "transform-register": TransformRegister,
  "transform-vip-register": TransformVipRegister,
  "transform-thank-you": TransformThankYou,
  "flex-your-alpha": FlexYourAlpha,

  // === New blocks (12 components) ===
  "hero-centered-dark": HeroCenteredDark,
  "hero-split-dark-label": HeroSplitDarkLabel,
  "features-card-grid-dark": FeaturesCardGridDark,
  "features-grid-3col-icons": FeaturesGrid3ColIcons,
  "features-grid-4col": FeaturesGrid4Col,
  "cta-split-image": CTASplitImage,
  "testimonials-slider-dark": TestimonialsSliderDark,
  "trial-cta-dark": TrialCtaDark,
  "author-profile": AuthorProfile,
  "cta-centered-buttons": CTACenteredButtons,
  "stats-centered": StatsCentered,
  "resources-hub": ResourcesHub,

  // === IGA sections (3 components) + features-accordion-image ===
  "iga-section-1": IGASection1,
  "iga-section-2": IGASection2,
  "iga-section-3": IGASection3,
  "features-accordion-image": FeaturesAccordionImage,


  // === Unique pages (4 components) ===
  "identity-maturity-assessment": IdentityMaturityAssessment,
  "igad-landing": IgadLanding,
  "ai-native-identity-section": AiNativeIdentitySection,
  "april-fools-game": AprilFoolsGame,

  // === Homepage ===
  "home-hero-2026": HomeHero2026,
  "partners-consulting-catalog": PartnersConsultingCatalog,

  // === Headless Identity Platform solution page ===
  "feature-dark-card-split": FeatureDarkCardSplit,
  "features-pink-icon-cards": FeaturesPinkIconCards,
  "features-heading-left-cards": FeaturesHeadingLeftCards,
  "features-dark-four-col-wireframe": FeaturesDarkFourColWireframe,
  "features-dark-step-row": FeaturesDarkStepRow,
  "features-horizontal-card-links": FeaturesHorizontalCardLinks,
  "testimonial-dark-portrait": TestimonialDarkPortrait,
  "cta-full-width-purple": CTAFullWidthPurple,
  "text-lede": TextLede,
  "stats-dark-promise": StatsDarkPromise,
  "solution-walkthrough-blade": SolutionWalkthroughBlade,

  // === Solutions Teams pages ===
  "features-problem-split": FeaturesProblemSplit,
  "features-why-dark": FeaturesWhyDark,
  "features-impact-split": FeaturesImpactSplit,

  // === Platform Overview page ===
  "statement-simple": StatementSimple,
  "statement-split-image": StatementSplitImage,
  "features-spotlight-grid": FeaturesSpotlightGrid,

  // === Integrations page animated diagram ===
  "connector-flow-animation": ConnectorFlowAnimation,
  "integrations-spotlight-grid": IntegrationsSpotlightGrid,


  // === C1 MCP Gateway direct-path animation ===
  "xaa-launch-animation": XaaLaunchAnimation,

  // === C1 MCP Gateway page ===
  "agent-access-fabric-animation": AgentAccessFabricAnimation,
  "features-accordion-split": FeaturesAccordionSplit,
};
